import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Flex,
  Divider,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { FaFileAlt } from 'react-icons/fa';
import SelectSearch from 'components/fields/SelectSearch';
import DateField from 'components/fields/DateField';
import { validateLicenseFormat } from 'views/quote_marketplace/constants/policyOptions';
import { formatDateToString } from 'utils/helpers';
import type { SelectOption } from 'components/fields/SelectSearch';
import type { APICarrierData } from 'types/api.types';
import type { MVRRequestTransactionType, MVRRequestPayload } from 'api';
import {
  getMvrRequestSchema,
  initialMvrRequestFormData,
  type MVRRequestFormData,
} from '../schemas';
import { useDriverList } from '../hooks/useDriverList';
import { useMvrCarrierRules } from '../hooks/useMvrCarrierRules';
import { useMvrCarriersForProvince } from '../hooks/useMvrCarriersForProvince';
import MvrDriverRow from './MvrDriverRow';
import MvrConfirmModal from './MvrConfirmModal';
import MvrProductTypeSelector from './MvrProductTypeSelector';
import MvrPortalAdvisory from './MvrPortalAdvisory';
import MvrOrderingBlockedStop from './MvrOrderingBlockedStop';
import MvrPolicyNumberBadge from './MvrPolicyNumberBadge';
import MvrFleetGarageAdvisory from './MvrFleetGarageAdvisory';
import { ErrorAlert } from 'components/shared/ErrorAlert';

interface MvrRequestTypeConfig {
  description: string;
  dateFieldLabel: string;
  showPolicyNumberField: boolean;
  showProductTypeField: boolean;
  showQuoteReferenceNumberField: boolean;
}

const MVR_REQUEST_TYPE_CONFIG: Record<
  MVRRequestTransactionType,
  MvrRequestTypeConfig
> = {
  PLPC: {
    description: 'Order an MVR for a personal lines policy change.',
    dateFieldLabel: 'Effective Date',
    showPolicyNumberField: true,
    showProductTypeField: false,
    showQuoteReferenceNumberField: false,
  },
  CLNB: {
    description: 'Order an MVR for a new commercial lines submission.',
    dateFieldLabel: 'Submission Date',
    showPolicyNumberField: false,
    showProductTypeField: true,
    showQuoteReferenceNumberField: true,
  },
  CLPC: {
    description: 'Order an MVR for a commercial lines policy change.',
    dateFieldLabel: 'Effective Date',
    showPolicyNumberField: true,
    showProductTypeField: true,
    showQuoteReferenceNumberField: false,
  },
};

interface MvrRequestFormProps {
  requestType: MVRRequestTransactionType;
  carriers: APICarrierData[];
  provinceOptions: SelectOption[];
  isLoadingProvinces: boolean;
  isSubmitting: boolean;
  onSubmit: (
    payload: MVRRequestPayload,
  ) => Promise<{ success: boolean; mvrOrderId?: number }>;
}

const MvrRequestForm = ({
  requestType,
  carriers,
  provinceOptions,
  isLoadingProvinces,
  isSubmitting,
  onSubmit,
}: MvrRequestFormProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const gateBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const driverCountBg = useColorModeValue('gray.500', 'whiteAlpha.400');
  const driverCountColor = useColorModeValue('white', 'whiteAlpha.900');
  const config = MVR_REQUEST_TYPE_CONFIG[requestType];
  const schema = useMemo(
    () =>
      getMvrRequestSchema({
        requiresPolicyNumber: config.showPolicyNumberField,
        requiresProductType: config.showProductTypeField,
        requiresQuoteReferenceNumber: config.showQuoteReferenceNumberField,
      }),
    [
      config.showPolicyNumberField,
      config.showProductTypeField,
      config.showQuoteReferenceNumberField,
    ],
  );

  const [formData, setFormData] = useState<MVRRequestFormData>(
    initialMvrRequestFormData,
  );
  const [isMvrOverridden, setIsMvrOverridden] = useState(false);
  const { drivers, addDriver, removeDriver, updateDriver, resetDrivers } =
    useDriverList();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleFieldChange = (field: keyof MVRRequestFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'province' || field === 'productType'
        ? { carrier: null }
        : {}),
    }));
    if (field === 'productType') {
      setIsMvrOverridden(false);
    }
  };

  const resetForm = () => {
    setFormData(initialMvrRequestFormData);
    setIsMvrOverridden(false);
    resetDrivers();
  };

  const showRestOfForm = !config.showProductTypeField || !!formData.productType;

  const driversAreValid = drivers.every(
    (driver) =>
      driver.name.trim() &&
      driver.licenceNumber.trim() &&
      formData.province !== null &&
      validateLicenseFormat(driver.licenceNumber, formData.province).valid,
  );

  const {
    carrierOptions: provinceCarrierOptions,
    carrierRules,
    isLoadingCarrierOptions,
    carrierOptionsError,
  } = useMvrCarriersForProvince({
    provinceId: formData.province,
    transactionType: requestType,
    productType: formData.productType,
    requiresProductType: config.showProductTypeField,
  });

  const { portalAdvisory, isBlocked, getPolicyNumberVerdict } =
    useMvrCarrierRules({
      carrierId: formData.carrier?.id ?? null,
      carrierRules,
    });
  const { showAdvisory, portalUrl } = portalAdvisory;
  const policyNumberVerdict = getPolicyNumberVerdict(formData.policyNumber);
  const isCarrierProvinceSelected =
    !!formData.carrier && formData.province !== null;
  const showFleetGarageAdvisory =
    requestType === 'CLNB' &&
    (formData.productType === 'fleet' || formData.productType === 'garage');
  const canShowFormFields = !showFleetGarageAdvisory || isMvrOverridden;
  const canShowOrderingSection = canShowFormFields && !isBlocked;
  const parseResult = useMemo(
    () => schema.safeParse(formData),
    [schema, formData],
  );
  const isFormValid = parseResult.success && driversAreValid;

  const handleConfirm = async (consentConfirmed: boolean) => {
    const payload: MVRRequestPayload = {
      transaction_type: requestType,
      carrier_id: formData.carrier.id,
      province: formData.province,
      ...(config.showPolicyNumberField
        ? { policy_number: formData.policyNumber }
        : {}),
      ...(config.showQuoteReferenceNumberField
        ? { quote_reference_number: formData.quoteReferenceNumber }
        : {}),
      ...(config.showProductTypeField
        ? { product_type: formData.productType }
        : {}),
      consent_confirmed: consentConfirmed,
      effective_date: formatDateToString(formData.date),
      drivers: drivers.map((driver) => ({
        name: driver.name,
        licence_number: driver.licenceNumber,
      })),
    };

    const result = await onSubmit(payload);
    if (result.success) {
      onClose();
      resetForm();
    }
  };

  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={4}>
        {config.description}
      </Text>

      <Stack spacing="6">
        {config.showProductTypeField && (
          <FormControl isRequired>
            <FormLabel color={textColor}>Product Type</FormLabel>
            <MvrProductTypeSelector
              selectedType={formData.productType}
              onSelect={(value) => handleFieldChange('productType', value)}
            />
          </FormControl>
        )}

        {showRestOfForm ? (
          <>
            {(formData.productType === 'fleet' ||
              formData.productType === 'garage') && (
              <MvrFleetGarageAdvisory
                show={showFleetGarageAdvisory}
                productType={formData.productType}
                isOverridden={isMvrOverridden}
                onOverride={() => setIsMvrOverridden(true)}
              />
            )}
            {canShowFormFields ? (
              <>
                <MvrPortalAdvisory
                  show={showAdvisory}
                  transactionType={requestType}
                  portalUrl={portalUrl}
                />
                <MvrOrderingBlockedStop show={isBlocked} />
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel color={textColor}>Province</FormLabel>
                    <SelectSearch
                      options={provinceOptions}
                      value={formData.province}
                      onChange={(value) =>
                        handleFieldChange(
                          'province',
                          value === null ? null : Number(value),
                        )
                      }
                      placeholder="Select province"
                      isLoading={isLoadingProvinces}
                      allowClear
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel color={textColor}>
                      Carrier
                      {isLoadingCarrierOptions && (
                        <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                          (Loading carriers...)
                        </Text>
                      )}
                    </FormLabel>
                    <SelectSearch
                      options={provinceCarrierOptions}
                      value={formData.carrier?.id || null}
                      onChange={(value) => {
                        const selectedCarrier =
                          carriers.find((c) => c.id === value) || null;
                        handleFieldChange('carrier', selectedCarrier);
                      }}
                      placeholder={
                        formData.province === null
                          ? 'Select province first'
                          : 'Select carrier'
                      }
                      isLoading={isLoadingCarrierOptions}
                      isDisabled={formData.province === null}
                      allowClear
                    />
                    {carrierOptionsError && (
                      <ErrorAlert
                        title="Failed to load carriers"
                        description="Please try again."
                      />
                    )}
                  </FormControl>

                  {config.showPolicyNumberField && (
                    <FormControl isRequired>
                      <Flex align="center" justify="space-between" mb={2}>
                        <FormLabel color={textColor} mb={0}>
                          Policy Number
                        </FormLabel>
                        <MvrPolicyNumberBadge
                          verdict={policyNumberVerdict.verdict}
                          expectedPatternText={
                            policyNumberVerdict.expectedPatternText
                          }
                        />
                      </Flex>
                      <Input
                        variant="main"
                        value={formData.policyNumber}
                        onChange={(e) =>
                          handleFieldChange('policyNumber', e.target.value)
                        }
                        placeholder={
                          isCarrierProvinceSelected
                            ? 'Enter policy number'
                            : 'Select carrier and province first'
                        }
                        isDisabled={!isCarrierProvinceSelected}
                      />
                    </FormControl>
                  )}

                  {config.showQuoteReferenceNumberField && (
                    <FormControl isRequired>
                      <FormLabel color={textColor}>
                        Quote or Reference Number
                      </FormLabel>
                      <Input
                        variant="main"
                        value={formData.quoteReferenceNumber}
                        onChange={(e) =>
                          handleFieldChange(
                            'quoteReferenceNumber',
                            e.target.value,
                          )
                        }
                        placeholder="Enter quote or reference number"
                      />
                    </FormControl>
                  )}

                  <Box>
                    <DateField
                      label={config.dateFieldLabel}
                      date={formData.date}
                      onChange={(date: Date | null) =>
                        handleFieldChange('date', date)
                      }
                      required
                      labelMl="10px"
                    />
                  </Box>
                </SimpleGrid>

                <Divider />

                {canShowOrderingSection && (
                  <Box>
                    <Flex align="center" gap={2} mb={3}>
                      <Text fontSize="md" fontWeight="700" color={textColor}>
                        Drivers
                      </Text>
                      <Center
                        bg={driverCountBg}
                        color={driverCountColor}
                        borderRadius="full"
                        boxSize="1.5rem"
                        fontSize="xs"
                        fontWeight="bold"
                      >
                        {drivers.length}
                      </Center>
                    </Flex>
                    <Stack spacing={2}>
                      {drivers.map((driver, index) => (
                        <MvrDriverRow
                          key={driver.id}
                          driver={driver}
                          index={index}
                          province={formData.province}
                          canRemove={drivers.length > 1}
                          onChange={(field, value) =>
                            updateDriver(driver.id, field, value)
                          }
                          onRemove={() => removeDriver(driver.id)}
                        />
                      ))}
                    </Stack>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<AddIcon />}
                      onClick={addDriver}
                      mt={3}
                    >
                      Add Driver
                    </Button>
                  </Box>
                )}

                <Flex justify="flex-end" gap={4}>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={resetForm}
                    isDisabled={isSubmitting}
                  >
                    Reset
                  </Button>
                  {canShowOrderingSection && (
                    <Button
                      colorScheme="brand"
                      onClick={onOpen}
                      leftIcon={<FaFileAlt />}
                      isDisabled={isLoadingCarrierOptions || !isFormValid}
                    >
                      Request MVR
                    </Button>
                  )}
                </Flex>
              </>
            ) : (
              <Box
                borderWidth="1px"
                borderStyle="dashed"
                borderColor={gateBorderColor}
                borderRadius="12px"
                p={8}
                textAlign="center"
              >
                <Text fontWeight="600" color={textColor}>
                  Acknowledge the advisory above
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Province, Carrier, and form fields will appear here
                </Text>
              </Box>
            )}
          </>
        ) : (
          <Box
            borderWidth="1px"
            borderStyle="dashed"
            borderColor={gateBorderColor}
            borderRadius="12px"
            p={8}
            textAlign="center"
          >
            <Text fontWeight="600" color={textColor}>
              Select a product type above
            </Text>
            <Text fontSize="sm" color="gray.500">
              Province, Carrier, and form fields will appear here
            </Text>
          </Box>
        )}
      </Stack>

      <MvrConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirm}
        isLoading={isSubmitting}
        drivers={drivers.map((driver) => ({
          id: driver.id,
          name: driver.name,
        }))}
        carrierName={formData.carrier?.name || ''}
      />
    </Box>
  );
};

export default MvrRequestForm;
